import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, Alert, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
import {
  CONTACTUS_TITLE1,
  CONTACTUS_TITLE2,
  CONTACTUS_TITLE3,
  EMAIL,
  FIRSTNAME,
  LASTNAME,
  LETSTALK,
  MESSAGE,
} from "@constants/unicomply-keys";
import { submitContactForm, ContactSubmission } from "@services/contact";
// import { useNavigate } from "react-router-dom";

const ContactSection = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #ffffff 0%, #f5fdff 100%)",
  padding: "100px 27px 100px 27px",
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.down("lg")]: {
    padding: "80px 20px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "60px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "40px 12px",
  },
}));

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "relative",
  alignItems: "stretch",
  maxWidth: "1200px",
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    gap: 16,
    maxWidth: "1000px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "center",
    gap: 24,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    gap: 20,
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 759,
  height: 637,
  borderRadius: "32px 0 0 32px",
  overflow: "hidden",
  border: "1px solid #ffffff",
  [theme.breakpoints.down("lg")]: {
    width: 640,
    height: 580,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: 759,
    height: "auto",
    minHeight: 400,
    borderRadius: 24,
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: 350,
    borderRadius: 20,
  },
}));

const GradientBackground = styled(Box)({
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse at 138px 40px, #065b70 0%, #0a829d 100%)",
  opacity: 0.9,
  backgroundImage: `url(${ContentUsBgImage})`,
  width: "100%",
  height: "100%",
  // Force full coverage to avoid tiny uncovered strip at the bottom on some devices
  backgroundSize: "100% 100%",
  backgroundPosition: "center bottom",
  backgroundRepeat: "no-repeat",
  minHeight: "100%",
  minWidth: "100%",
});

const BackgroundPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.2,
});

const LeftContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 10,
  padding: "68px 99px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  // minHeight: "100%",
  [theme.breakpoints.down("lg")]: {
    padding: "50px 60px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "40px 32px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "30px 20px",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: 16,
  border: "1px solid #1d4c50",
  height: 637,
  padding: "0 64px",
  display: "flex",
  alignItems: "center",
  position: "relative",
  marginLeft: -104,
  zIndex: 10,
  minWidth: 0, // Allow flex shrinking
  [theme.breakpoints.down("lg")]: {
    marginLeft: -32,
    padding: "0 40px",
    height: 580,
  },
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    maxWidth: 600,
    height: "auto",
    padding: "40px 32px",
    borderRadius: 20,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "30px 20px",
    borderRadius: 16,
    margin: "0 6px",
    maxWidth: "90%",
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: 450,
  [theme.breakpoints.down("lg")]: {
    width: 400,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
  },
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: 0,
    border: "none",
    borderBottom: "1px solid #065b70",
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "& input": {
      padding: "16px 12px",
      height: "18px",
      [theme.breakpoints.down("sm")]: {
        padding: "14px 10px",
        fontSize: "16px", // Prevent zoom on iOS
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 600,
    color: "#1f2937",
    fontSize: 14,
    lineHeight: "20px",
    transform: "translate(12px, -9px) scale(1)",
    [theme.breakpoints.down("sm")]: {
      fontSize: 13,
      transform: "translate(10px, -9px) scale(1)",
    },
  },
}));

const CustomTextArea = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: 0,
    border: "none",
    borderBottom: "1px solid #065b70",
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "& textarea": {
      padding: "12px 12px",
      resize: "none",
      paddingLeft: "0px",
      minHeight: "30px",
      maxHeight: "80px",
      [theme.breakpoints.down("sm")]: {
        padding: "10px 10px",
        paddingLeft: "0px",
        fontSize: "16px", // Prevent zoom on iOS
        minHeight: "25px",
        maxHeight: "70px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 600,
    color: "#1f2937",
    fontSize: 14,
    lineHeight: "20px",
    transform: "translate(12px, 16px) scale(1)",
    [theme.breakpoints.down("sm")]: {
      fontSize: 13,
      transform: "translate(10px, 14px) scale(1)",
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#0A829D",
  color: "#fff",
  fontWeight: 600,
  fontSize: 16,
  lineHeight: "24px",
  padding: "16px 20px",
  borderRadius: 8,
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  textTransform: "none",
  minHeight: 48,
  "&:hover": {
    backgroundColor: "#0a829d",
    color: "#ffffff",
  },
  transition: "all 0.2s ease",
  [theme.breakpoints.down("sm")]: {
    padding: "14px 18px",
    fontSize: 15,
    minHeight: 44,
  },
}));

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  captchaAnswer: string;
}

export default function UniComply_Contact() {
  // theme hook not used here; remove to satisfy linter
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    captchaAnswer: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [captchaData, setCaptchaData] = useState<{
    question: string;
    answer: string;
    type: 'visual';
  } | null>(null);
  const [captchaError, setCaptchaError] = useState<string>("");
  // const navigate = useNavigate();

  // Generate visual CAPTCHA with scrambled characters
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const length = 6 + Math.floor(Math.random() * 3); // 6-8 characters
    let answer = '';
    
    // Generate random characters
    for (let i = 0; i < length; i++) {
      answer += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Create scrambled display (no spaces)
    const scrambled = answer.split('').map((char) => ({ char })).sort(() => Math.random() - 0.5);
    
    const displayText = scrambled.map(item => item.char).join('');
    
    // Store the clean answer (without spaces/superscript formatting) for validation
    const cleanAnswer = scrambled.map(item => item.char).join('');
    
    setCaptchaData({ 
      question: displayText, 
      answer: cleanAnswer, 
      type: 'visual' 
    });
    setCaptchaError("");
  };

  // Generate CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
    
    // Clear CAPTCHA error when user starts typing
    if (captchaError) {
      setCaptchaError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all fields'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        type: 'error',
        message: 'Please enter a valid email address'
      });
      return;
    }

    // CAPTCHA validation
    if (!formData.captchaAnswer.trim()) {
      setCaptchaError("Please solve the CAPTCHA");
      setSubmitStatus({
        type: 'error',
        message: 'Please solve the CAPTCHA to continue'
      });
      return;
    }

    const userAnswer = formData.captchaAnswer.trim().toUpperCase();
    
    if (!captchaData) {
      setCaptchaError("CAPTCHA not loaded. Please refresh the page.");
      setSubmitStatus({
        type: 'error',
        message: 'CAPTCHA not loaded. Please refresh the page.'
      });
      return;
    }
    
    const correctAnswer = captchaData.answer.toUpperCase();
    
    // Debug logging
    console.log('User answer:', userAnswer);
    console.log('Correct answer:', correctAnswer);
    console.log('CAPTCHA question:', captchaData.question);
    
    if (userAnswer !== correctAnswer) {
      setCaptchaError("Invalid CAPTCHA. Please try again.");
      setSubmitStatus({
        type: 'error',
        message: 'CAPTCHA verification failed. Please try again.'
      });
      generateCaptcha(); // Generate new CAPTCHA
      setFormData(prev => ({ ...prev, captchaAnswer: "" }));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const contactData: ContactSubmission = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };

      await submitContactForm(contactData);
      
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your message! We\'ll get back to you soon.'
      });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        message: "",
        captchaAnswer: "",
      });
      generateCaptcha(); // Generate new CAPTCHA for next submission

    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <Box sx={{ position: "relative" }}>
        <MainContainer>
          {/* Left Panel - Branded Content */}
          <Box sx={{ position: "relative" }}>
            {/* Black background frame */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#000000",
                borderRadius: { xs: "20px", md: "32px" },
                border: "1px solid #ffffff",
                width: { xs: "100%", lg: "100%", xl: 758.5 },
                height: { xs: "99.50%", lg: "99.50%%", xl: "99.50%" },
                maxWidth: { xs: "759px", md: "100%" },
              }}
            />

            <LeftPanel>
              <GradientBackground />

              {/* Background patterns - simplified for now */}
              <BackgroundPattern>
                <Box
                  sx={{
                    position: "absolute",
                    top: "5.2%",
                    left: "-188.9%",
                    width: 2274.63,
                    height: 828.745,
                    transform: "rotate(180deg)",
                    background:
                      "radial-gradient(ellipse at center, rgba(255,255,255,0.1) 0%, transparent 70%)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "-15.2%",
                    left: "-236.12%",
                    width: 2558.7,
                    height: 714.648,
                    transform: "rotate(180deg)",
                    background:
                      "radial-gradient(ellipse at center, rgba(255,255,255,0.05) 0%, transparent 70%)",
                  }}
                />
              </BackgroundPattern>

              <LeftContent>
                {/* Tagline */}
                <Typography
                  sx={{
                    color: "#E3E3E3",
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: { xs: "14px", sm: "15px" },
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "normal",
                  }}
                >
                  {CONTACTUS_TITLE1}
                </Typography>

                {/* Main heading */}
                <Typography
                  sx={{
                    color: "#FFF",
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: { xs: "40px", sm: "52px", md: "64px", lg: "74px" },
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "normal",
                    marginBottom: 12,
                    mt: 12,
                  }}
                >
                  {CONTACTUS_TITLE2}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: "var(--White, #FFF)",
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontSize: { xs: "14px", sm: "15px" },
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "150%", // 30px
                    width: { xs: "100%", sm: "400px" },
                  }}
                >
                  {CONTACTUS_TITLE3}
                </Typography>
              </LeftContent>
            </LeftPanel>
          </Box>

          {/* Right Panel - Contact Form */}
          <RightPanel>
            <FormContainer>
              {/* Status Alert */}
              {submitStatus && (
                <Alert 
                  severity={submitStatus.type} 
                  sx={{ 
                    mb: 2, 
                    p: 0.5, 
                    maxWidth: "95%",
                    borderRadius: "18px",
                    padding: "0px 8px"
                  }}
                  onClose={() => setSubmitStatus(null)}
                >
                  {submitStatus.message}
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* Name Fields Row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: { xs: 2, sm: 2.5, md: 3 },
                    marginBottom: { xs: 3, md: 4 },
                    marginTop: { xs: 2, md: 3 },
                  }}
                >
                  <CustomTextField
                    label={FIRSTNAME}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                  <CustomTextField
                    label={LASTNAME}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                </Box>

                {/* Email Field */}
                <Box sx={{ marginBottom: { xs: 3, md: 4 } }}>
                  <CustomTextField
                    label={EMAIL}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                </Box>

                {/* Message Field */}
                <Box sx={{ marginBottom: { xs: 3, md: 4 } }}>
                  <CustomTextArea
                    label={MESSAGE}
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    multiline
                    minRows={4}
                    maxRows={5}
                    required
                    fullWidth
                    disabled={isSubmitting}
                  />
                </Box>

                {/* Visual CAPTCHA Section */}
                <Box sx={{ marginBottom: 3 }}>
                  <Typography sx={{ 
                    fontWeight: 600, 
                    color: '#495057',
                    fontSize: '14px',
                    marginBottom: 1
                  }}>
                    Security Verification:
                  </Typography>
                  
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 1.5, sm: 2 },
                    marginBottom: 1,
                  }}>
                    {/* Top row for CAPTCHA display and reload button */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: { xs: '100%', sm: 'auto' },
                    }}>
                      {/* CAPTCHA Display Field */}
                      <Box sx={{
                        flex: { xs: 1, sm: 'none' },
                        width: { xs: 'auto', sm: '200px' },
                        padding: '8px 12px',
                        border: '2px solid #e74c3c',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        fontFamily: 'monospace',
                        fontSize: { xs: '13px', sm: '16px' },
                        fontWeight: 600,
                        letterSpacing: '1px',
                        color: '#2c3e50',
                        overflow: 'hidden',
                        textAlign: 'center',
                      }}>
                        {captchaData ? captchaData.question : 'Loading...'}
                      </Box>

                      {/* Reload Button */}
                      <Button
                        onClick={generateCaptcha}
                        disabled={isSubmitting}
                        sx={{
                          minWidth: '40px',
                          height: '40px',
                          backgroundColor: '#27ae60',
                          color: 'white',
                          borderRadius: '6px',
                          fontSize: '18px',
                          fontWeight: 600,
                          textTransform: 'none',
                          flexShrink: 0,
                          '&:hover': {
                            backgroundColor: '#229954'
                          },
                          '&:disabled': {
                            backgroundColor: '#bdc3c7'
                          }
                        }}
                      >
                        ↻
                      </Button>
                    </Box>

                    {/* CAPTCHA Answer Input */}
                    <CustomTextField
                      label="Enter CAPTCHA"
                      name="captchaAnswer"
                      value={formData.captchaAnswer}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      error={!!captchaError}
                      helperText={captchaError}
                      placeholder="e.g., A4B7K9"
                      sx={{
                        width: { xs: '100%', sm: '200px' },
                        '& .MuiFormLabel-root': {
                          fontSize: { xs: '12px', sm: '13px' }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '11px',
                          marginLeft: 0,
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Submit Button */}
                <SubmitButton 
                  type="submit" 
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isSubmitting ? 'Sending...' : LETSTALK}
                </SubmitButton>
              </form>
            </FormContainer>
          </RightPanel>
        </MainContainer>
      </Box>
    </ContactSection>
  );
}
